import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { themeContext } from "../../context/theme";
import { CardOne } from "../../components/cards/cardOne";
import { YouWon } from "../../components/svg/youWon";
import { Tie } from "../../components/svg/tie";
import { CardsFlying } from "../../components/svg/cardsFlying";
import { GoBackButton } from "../../components/buttons/goBack";
import { useNetworkService } from "../../hooks/useNetworkService";
import { useGamepad } from "../../hooks/useGamepad";
import { playSound } from "../../utilities/utilities";
import styles from "./OnlineGameResult.module.css";

export const OnlineGameResult = ({
  gameResults,
  playerId,
  onReturnToLobby,
  game,
}) => {
  const { theme } = useContext(themeContext);
  const themeNumber = theme.replace("theme-", "");
  const { sendGameEvent, returnToLobby } = useNetworkService();
  const [animatedScores, setAnimatedScores] = useState({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [isReturningToLobby, setIsReturningToLobby] = useState(false);
  const [isUIReady, setIsUIReady] = useState(false);
  const mainCardRef = useRef(null);
  const isMouseModeRef = useRef(false);

  // Focusable elements for keyboard/gamepad navigation
  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  // Get game mode info from results
  const { gameMode = "survival", winCondition = "last_survivor" } =
    gameResults || {};

  // Use pre-sorted rankings from game results (already sorted by game mode logic)
  const rankings = gameResults?.players || [];

  const winner = rankings[0];
  const isLocalPlayerWinner = winner?.id === playerId;

  // Determine tie based on win condition
  let isTie = false;
  if (winCondition === "highest_score") {
    // In score-based modes, tie is determined by same score
    const topScore = winner?.score || winner?.stats?.score || 0;
    const topPlayers = rankings.filter((p) => {
      const playerScore = p.score || p.stats?.score || 0;
      return playerScore === topScore;
    });
    isTie = topPlayers.length > 1;
  } else {
    // In survival modes, check if multiple survivors
    const alivePlayers = rankings.filter((p) => !p.isGameOver);
    isTie = alivePlayers.length > 1;
  }

  // Get game mode display info
  const getGameModeInfo = (mode) => {
    switch (mode) {
      case "survival":
        return {
          name: "Survival",
          icon: "🔥",
          color: "#FF5722",
        };
      case "speed_battle":
        return {
          name: "Speed Battle",
          icon: "⚡",
          color: "#FF9800",
        };
      default:
        return {
          name: "Survival",
          icon: "🔥",
          color: "#FF5722",
        };
    }
  };

  const gameModeInfo = getGameModeInfo(gameMode);

  // Animate scores - only run once when results first appear
  useEffect(() => {
    if (rankings.length === 0 || Object.keys(animatedScores).length > 0) return;

    const timers = [];

    const animateScore = (playerId, targetScore) => {
      let currentScore = 0;
      const increment = Math.max(1, Math.floor(targetScore / 50));

      const timer = setInterval(() => {
        currentScore += increment;
        if (currentScore >= targetScore) {
          currentScore = targetScore;
          clearInterval(timer);
        }
        setAnimatedScores((prev) => ({
          ...prev,
          [playerId]: currentScore,
        }));
      }, 50); // Slower animation for better visual effect

      timers.push(timer);
    };

    rankings.forEach((player) => {
      // Check both player.score and player.stats.score for compatibility
      const playerScore = player.score || player.stats?.score || 0;
      animateScore(player.id, playerScore);
    });

    // Show confetti and play celebration sounds for winner
    if (isLocalPlayerWinner) {
      setShowConfetti(true);

      // Play winning sound immediately
      game?.sounds?.winningSound?.play();

      // Add celebration sound sequence
      setTimeout(() => {
        // Play claps after a short delay
        game?.sounds?.clapsSound?.play();
      }, 800);

      setTimeout(() => {
        // Play wow sound for extra celebration
        if (Math.random() > 0.5) {
          game?.sounds?.wowSound?.play();
        } else {
          game?.sounds?.niceSound?.play();
        }
      }, 1600);
    } else {
      game?.sounds?.gameOverSound?.play();
    }

    // Cleanup function
    return () => {
      timers.forEach((timer) => clearInterval(timer));
    };
  }, [rankings.length, isLocalPlayerWinner, game]); // Only depend on rankings.length, not the full rankings array

  const handleReturnToLobby = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    // Prevent multiple rapid clicks
    if (isReturningToLobby) {
      return;
    }

    // Play click sound
    playSound(game, "click");

    setIsReturningToLobby(true);

    try {
      // Use dedicated return to lobby method to sync room state
      await returnToLobby();
    } catch (error) {
      // Continue anyway - the room state will sync eventually
    } finally {
      // Always call the parent handler regardless of network result
      onReturnToLobby();
      // Reset the flag after a delay to prevent multiple calls
      setTimeout(() => setIsReturningToLobby(false), 1000);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Wait for UI animation to complete before accepting gamepad input
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsUIReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Initialize focusable elements and focus the main container
  useEffect(() => {
    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      const allElements = document.querySelectorAll("[tabindex='0']");
      const elements = Array.from(allElements).filter(el => !el.disabled);
      focusableElements.elements = elements;
      if (elements.length > 0) {
        focusableElements.index = 0;
        // Focus the main card container to enable keyboard events
        if (mainCardRef.current) {
          mainCardRef.current.focus();
        }
        isMouseModeRef.current = false;
      }
    }, 100);
  }, [focusableElements]);

  // Handle gamepad navigation
  const handleGamepadNavigation = useCallback((direction) => {
    // Switch to keyboard mode
    isMouseModeRef.current = false;

    const allElements = document.querySelectorAll("[tabindex='0']");
    const elements = Array.from(allElements).filter(el => !el.disabled);
    if (elements.length === 0) return;

    focusableElements.elements = elements;

    if (focusableElements.index === undefined || focusableElements.index >= elements.length) {
      focusableElements.index = 0;
    }

    if (direction === 'down') {
      if (focusableElements.index >= elements.length - 1) {
        focusableElements.index = 0;
      } else {
        focusableElements.index++;
      }
    } else if (direction === 'up') {
      if (focusableElements.index <= 0) {
        focusableElements.index = elements.length - 1;
      } else {
        focusableElements.index--;
      }
    }

    if (elements[focusableElements.index]) {
      playSound(game, "navigation");
      elements[focusableElements.index].focus();
    }
  }, [focusableElements, game]);

  // Handle gamepad button presses
  const handleGamepadButtonPress = useCallback((button) => {
    if (button === 'A') {
      if (focusableElements.elements && focusableElements.elements[focusableElements.index]) {
        playSound(game, "click");
        focusableElements.elements[focusableElements.index].click();
      }
    }
  }, [game, focusableElements]);

  // Use the useGamepad hook
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: isUIReady,
    gamepadIndex: null
  });

  return (
    <div className={styles.pageContainer}>
      {showConfetti && <CardsFlying />}

      <CardOne
        customTag="section"
        attributes={{
          ref: mainCardRef,
          className: styles.mainCard,
          onKeyDown: (event) => {
            const isNavigationKey = event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter';

            if (isNavigationKey) {
              // Switch to keyboard mode
              isMouseModeRef.current = false;

              const allElements = document.querySelectorAll("[tabindex='0']");
              const elements = Array.from(allElements).filter(el => !el.disabled);
              if (elements.length > 0) {
                focusableElements.elements = elements;

                // Find current focused element's index
                const currentIndex = elements.indexOf(document.activeElement);

                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  let newIndex;
                  if (currentIndex === -1) {
                    newIndex = 0;
                  } else if (currentIndex >= elements.length - 1) {
                    newIndex = 0;
                  } else {
                    newIndex = currentIndex + 1;
                  }
                  focusableElements.index = newIndex;
                  if (elements[newIndex]) {
                    playSound(game, "navigation");
                    elements[newIndex].focus();
                  }
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  let newIndex;
                  if (currentIndex === -1) {
                    newIndex = elements.length - 1;
                  } else if (currentIndex <= 0) {
                    newIndex = elements.length - 1;
                  } else {
                    newIndex = currentIndex - 1;
                  }
                  focusableElements.index = newIndex;
                  if (elements[newIndex]) {
                    playSound(game, "navigation");
                    elements[newIndex].focus();
                  }
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  if (currentIndex !== -1 && elements[currentIndex]) {
                    playSound(game, "click");
                    elements[currentIndex].click();
                  }
                }
              }
            }
          },
          onMouseMove: (event) => {
            if (isMouseModeRef.current !== false) {
              // Switch to mouse mode
              isMouseModeRef.current = true;

              const allElements = document.querySelectorAll("[tabindex='0']");
              const elements = Array.from(allElements).filter(el => !el.disabled);
              if (elements.length > 0) {
                focusableElements.elements = elements;
                // When hovering a new element, blur ALL focused elements (mouse mode)
                focusableElements?.elements?.forEach((element, index) => {
                  if (event.target === element && index !== focusableElements.index) {
                    // Blur all elements when in mouse mode
                    focusableElements.elements.forEach(el => {
                      if (el === document.activeElement) {
                        el.blur();
                      }
                    });
                    focusableElements.index = index;
                  }
                });
              }
            }
          },
          tabIndex: -1
        }}
      >
        <div className={styles.resultContainer}>
          {/* Winner Announcement */}
          <div className={styles.winnerSection}>
            {isLocalPlayerWinner && !isTie && <YouWon />}
            {isLocalPlayerWinner && isTie && <Tie />}
            {!isLocalPlayerWinner && !isTie && (
              <div className={styles.gameOverText}>
                <p className={styles.winnerName}>
                  🏆 {winner?.name || "Unknown"} Won!
                </p>
              </div>
            )}
            {!isLocalPlayerWinner && isTie && (
              <div className={styles.gameOverText}>
                <Tie />
                <p className={styles.tieText}>It's a tie!</p>
              </div>
            )}
          </div>

          {/* Game Mode Info */}
          <div className={styles.gameModeSection}>
            <div
              className={styles.gameModeHeader}
              style={{ borderColor: gameModeInfo.color }}
            >
              <span className={styles.gameModeIcon}>{gameModeInfo.icon}</span>
              <h3 className={styles.gameModeName}>{gameModeInfo.name}</h3>
            </div>
          </div>

          {/* Rankings Table */}
          <div className={styles.rankingsSection}>
            <h3 className={styles[`rankingsTitle-${theme}`]}>Final Rankings</h3>
            <div className={styles.rankingsTable}>
              {rankings.map((player, index) => (
                <div
                  key={player.id}
                  className={`${styles.playerRank} ${
                    player.id === playerId
                      ? styles[`currentPlayer-theme-${theme}`]
                      : ""
                  }`}
                >
                  <div className={styles.rankNumber}>
                    {index === 0
                      ? "🏆"
                      : index === 1
                      ? "🥈"
                      : index === 2
                      ? "🥉"
                      : `#${index + 1}`}
                  </div>

                  <div className={styles.playerInfo}>
                    <div className={styles.playerName}>
                      {player.name || `Player ${player.id.slice(0, 8)}`}
                      {player.id === playerId && " (You)"}
                    </div>
                    <div className={styles.playerStatus}>
                      {player.isGameOver ? "💀 Game Over" : "✅ Survived"}
                    </div>
                  </div>

                  <div className={styles.playerStats}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Score</span>
                      <span className={styles.statValue}>
                        {animatedScores[player.id] || 0}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>1x</span>
                      <span className={styles.statValue}>
                        {player.singleLines || player.stats?.singleLines || 0}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>2x</span>
                      <span className={styles.statValue}>
                        {player.doubleLines || player.stats?.doubleLines || 0}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>3x</span>
                      <span className={styles.statValue}>
                        {player.tripleLines || player.stats?.tripleLines || 0}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>4x</span>
                      <span className={styles.statValue}>
                        {player.tetrisLines || player.stats?.tetrisLines || 0}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Total Lines</span>
                      <span className={styles.statValue}>
                        {player.lines || player.stats?.linesCleared || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionsSection}>
            <button
              onClick={handleReturnToLobby}
              onMouseDown={(e) => e.preventDefault()}
              className={`${styles.returnButton} ${styles[`returnButton-theme-${themeNumber}`]}`}
              disabled={isReturningToLobby}
              tabIndex="0"
            >
              🏠 Return to Lobby
            </button>
          </div>
        </div>
      </CardOne>
    </div>
  );
};
